// Premium Propulse Quiz Battle — Kahoot-style colored answer panels, big circular timer,
// streak combos, confetti on correct, fireworks at finish, learning insight on each answer.
import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Timer, Trophy, Volume2, VolumeX, RefreshCw, CheckCircle2, XCircle, Zap, Flame } from 'lucide-react';
import { GameFrame, AnnouncementBanner, ScoreTile, playGameSound, GoldButton } from './shared/GameFX';
import { ConfettiBurst, Fireworks } from './ludo/LudoConfetti';

const TIME_PER_Q = 15;

const OPTION_THEMES = [
    { from: '#E31E24', to: '#7F1010', shape: '▲' },
    { from: '#3B82F6', to: '#1E3A8A', shape: '◆' },
    { from: '#F59E0B', to: '#92400E', shape: '●' },
    { from: '#10B981', to: '#064E3B', shape: '■' },
];

export default function QuizBattle({ game, onPlayed }) {
    const questions = game.config?.questions || [];
    const [idx, setIdx] = useState(0);
    const [time, setTime] = useState(TIME_PER_Q);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [done, setDone] = useState(false);
    const [picked, setPicked] = useState(null);
    const [muted, setMuted] = useState(false);
    const [announce, setAnnounce] = useState(null);
    const [bursts, setBursts] = useState([]);
    const [showFireworks, setShowFireworks] = useState(false);
    const burstIdRef = useRef(0);

    const triggerBurst = () => {
        burstIdRef.current += 1;
        const id = burstIdRef.current;
        setBursts((b) => [...b, { id, count: 25, scale: 1.0 }]);
        setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1500);
    };

    const announce_ = (text, tone = 'green') => {
        const id = Date.now() + Math.random();
        setAnnounce({ text, tone, id });
        setTimeout(() => setAnnounce((a) => (a && a.id === id ? null : a)), 1300);
    };

    useEffect(() => {
        if (done || picked) return;
        if (time <= 0) { next(false); return; }
        const id = setTimeout(() => setTime((t) => t - 1), 1000);
        return () => clearTimeout(id);
        // eslint-disable-next-line
    }, [time, done, picked]);

    const next = (correct) => {
        const timeBonus = Math.max(0, Math.floor(time / 2));
        const basePoints = correct ? 10 : 0;
        const streakBonus = correct ? streak * 2 : 0;
        const gained = basePoints + (correct ? timeBonus : 0) + streakBonus;
        const newScore = score + gained;
        setScore(newScore);
        if (correct) {
            const newStreak = streak + 1;
            setStreak(newStreak);
            setBestStreak((bs) => Math.max(bs, newStreak));
        } else {
            setStreak(0);
        }
        if (idx >= questions.length - 1) {
            setDone(true);
            playGameSound('win', muted);
            setShowFireworks(true);
            setTimeout(() => setShowFireworks(false), 4500);
            const maxScore = questions.length * 10;
            const finalPct = Math.round((newScore / maxScore) * 100);
            const points = Math.max(5, Math.round(Math.min(100, finalPct) * game.points_per_play / 100));
            api.post(`/games/${game.code}/submit`, { score: Math.min(100, finalPct), awarded_points: points, details: { questions: questions.length, bestStreak } }).then(() => onPlayed && onPlayed());
        } else {
            setIdx(idx + 1);
            setTime(TIME_PER_Q);
            setPicked(null);
        }
    };

    const choose = (opt) => {
        if (picked !== null) return;
        const q = questions[idx];
        const correct = opt === q.correct;
        setPicked({ opt, correct });
        if (correct) {
            playGameSound('correct', muted);
            triggerBurst();
            const newStreak = streak + 1;
            announce_(newStreak > 2 ? `🔥 ${newStreak}-STREAK!` : '✅ Correct!', 'green');
        } else {
            playGameSound('wrong', muted);
            announce_('❌ Wrong', 'red');
        }
        setTimeout(() => next(correct), 1200);
    };

    const reset = () => {
        setIdx(0); setScore(0); setDone(false); setPicked(null);
        setTime(TIME_PER_Q); setStreak(0); setBestStreak(0); setShowFireworks(false);
    };

    if (done) {
        const maxScore = questions.length * 10;
        return (
            <GameFrame dataTestId="quizbattle-done" orbs={[
                { color: '#10b981', x: '15%', y: '20%', size: 230, delay: 0 },
                { color: '#FFF200', x: '85%', y: '25%', size: 210, delay: 1.4 },
                { color: '#3b82f6', x: '50%', y: '85%', size: 240, delay: 2.8 },
            ]}>
                <div className="text-center py-8" data-testid="qb-final">
                    <motion.div animate={{ y: [-4, 4, -4] }} transition={{ repeat: Infinity, duration: 1.6 }}>
                        <Trophy className="h-16 w-16 mx-auto text-amber-400 drop-shadow" />
                    </motion.div>
                    <div className="font-display font-extrabold text-4xl text-white mt-4">{score} <span className="text-2xl text-slate-400">/ {maxScore}+</span></div>
                    <div className="text-sm text-slate-300 mt-1">Quiz Battle complete · best streak <b className="text-amber-300">×{bestStreak}</b></div>
                    <div className="mt-6 flex justify-center">
                        <GoldButton onClick={reset} dataTestId="qb-play-again-btn">
                            <RefreshCw className="h-4 w-4" /> Play Again
                        </GoldButton>
                    </div>
                </div>
                {showFireworks && <Fireworks duration={4.5} density={1.5} />}
            </GameFrame>
        );
    }

    const q = questions[idx];
    const progressPct = ((idx) / questions.length) * 100;

    return (
        <GameFrame dataTestId="quizbattle-game" orbs={[
            { color: '#3b82f6', x: '12%', y: '20%', size: 220, delay: 0 },
            { color: '#E31E24', x: '85%', y: '18%', size: 200, delay: 1.4 },
            { color: '#FFF200', x: '50%', y: '90%', size: 240, delay: 2.8 },
        ]}>
            <Header muted={muted} onToggleMute={() => setMuted((m) => !m)}
                idx={idx} total={questions.length} streak={streak} score={score} />

            {/* Progress bar */}
            <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div animate={{ width: `${progressPct}%` }} className="h-full bg-gradient-to-r from-amber-400 to-yellow-500" />
            </div>

            <div className="mt-5 flex items-center justify-between">
                {/* Big circular timer */}
                <CircularTimer time={time} max={TIME_PER_Q} />
                {streak >= 2 && (
                    <motion.div
                        initial={{ scale: 0 }} animate={{ scale: 1 }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white font-display font-extrabold text-sm shadow-lg"
                        style={{ boxShadow: '0 0 20px rgba(244,63,94,0.5)' }}>
                        <Flame className="h-4 w-4" /> ×{streak} STREAK
                    </motion.div>
                )}
            </div>

            <AnimatePresence mode="wait">
                <motion.div key={idx}
                    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                    className="mt-5">
                    <div className="font-display font-extrabold text-xl sm:text-2xl text-white text-center leading-snug" data-testid="qb-question">
                        {q.question}
                    </div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-3">
                        {q.options.map((opt, oi) => {
                            const theme = OPTION_THEMES[oi % OPTION_THEMES.length];
                            const isPicked = picked?.opt === opt;
                            const isCorrect = picked && opt === q.correct;
                            const cls = !picked
                                ? 'hover:scale-105 active:scale-95'
                                : isCorrect ? 'ring-4 ring-emerald-300 scale-105'
                                    : isPicked ? 'ring-4 ring-rose-300 opacity-70'
                                        : 'opacity-40 grayscale';
                            return (
                                <motion.button
                                    key={oi}
                                    onClick={() => choose(opt)}
                                    disabled={!!picked}
                                    whileTap={{ scale: 0.97 }}
                                    className={`text-left px-4 py-4 rounded-2xl text-white font-display font-extrabold text-base sm:text-lg transition-all flex items-center gap-3 ${cls}`}
                                    style={{
                                        background: `linear-gradient(135deg, ${theme.from} 0%, ${theme.to} 100%)`,
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.4), inset 0 2px 0 rgba(255,255,255,0.3), inset 0 -3px 0 rgba(0,0,0,0.25)',
                                    }}
                                    data-testid={`qb-opt-${oi}`}>
                                    <span className="text-2xl drop-shadow">{theme.shape}</span>
                                    <span className="flex-1">{opt}</span>
                                    {picked && isCorrect && <CheckCircle2 className="h-6 w-6" />}
                                    {picked && isPicked && !isCorrect && <XCircle className="h-6 w-6" />}
                                </motion.button>
                            );
                        })}
                    </div>
                </motion.div>
            </AnimatePresence>

            {bursts.map((b) => <ConfettiBurst key={b.id} count={b.count} scale={b.scale} />)}
            <AnnouncementBanner announce={announce} />
        </GameFrame>
    );
}

function CircularTimer({ time, max }) {
    const pct = (time / max) * 100;
    const circumference = 2 * Math.PI * 22;
    const offset = circumference - (pct / 100) * circumference;
    const danger = time <= 5;
    return (
        <div className="relative w-14 h-14 sm:w-16 sm:h-16" data-testid="quizbattle-timer">
            <svg viewBox="0 0 50 50" className="w-full h-full -rotate-90">
                <circle cx="25" cy="25" r="22" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
                <motion.circle cx="25" cy="25" r="22" fill="none"
                    stroke={danger ? '#ef4444' : '#FFD700'}
                    strokeWidth="4" strokeLinecap="round"
                    strokeDasharray={circumference}
                    animate={{ strokeDashoffset: offset }}
                    transition={{ duration: 0.5, ease: 'linear' }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <motion.div
                    animate={danger ? { scale: [1, 1.15, 1] } : { scale: 1 }}
                    transition={danger ? { repeat: Infinity, duration: 0.6 } : { duration: 0 }}
                    className={`font-display font-extrabold text-lg sm:text-xl ${danger ? 'text-rose-400' : 'text-white'}`}>
                    {time}
                </motion.div>
                <div className="text-[8px] uppercase tracking-wider text-slate-400 -mt-1">sec</div>
            </div>
        </div>
    );
}

function Header({ muted, onToggleMute, idx, total, streak, score }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center"
                style={{ boxShadow: '0 0 20px rgba(59,130,246,0.5)' }}>
                <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-sky-300">Propulse Cables & Wires</div>
                <div className="font-display font-extrabold text-lg sm:text-2xl text-white truncate">Quiz Battle</div>
                <div className="text-[11px] sm:text-xs text-slate-400 truncate">Speed + accuracy = streak bonus points</div>
            </div>
            <div className="hidden sm:flex gap-2">
                <ScoreTile label="Q" value={`${idx + 1}/${total}`} accent="from-sky-400 to-blue-500" />
                <ScoreTile label="Pts" value={score} accent="from-amber-400 to-yellow-500" />
                <ScoreTile label="Streak" value={`×${streak}`} accent="from-rose-400 to-rose-600" />
            </div>
            <button onClick={onToggleMute} className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="quizbattle-mute-btn">
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
        </div>
    );
}
